<template>
    <v-expansion-panels
        role="tablist"
        focusable
        data-testid="service-panel"
        id="service-panel"
    >
        <v-expansion-panel
            v-for="(service) in filteredServices"
            hide-default-footer
            class="v-expansion-panel-odd-background"
            :class="{'active-header-background': currentPanel === service.id}"
            role="tab"
            :key="service.id"
            @click.native=" currentPanel = service.id "
        >
            <!-- <div slot="header"> @TODO Need to fix this and add it to the div below -->
            <v-expansion-panel-header :disabled="isLoading" tabindex="-1" role="group">
                <div>
                    <v-layout align-center>
                        <!-- Title of service -->
                        <v-flex
                            xs12
                            :md9="stepNumber > 1"
                            :md11="stepNumber <= 1"
                            align-self-center
                            class="text-center text-md-left py-3 py-md-0">
                            <v-tooltip top>
                                <template v-slot:activator="{ on }">
                                    <v-btn
                                        role="tabpanel"
                                        tabindex="-1"
                                        class="px-0 d-inline-block text-wrap"
                                        text
                                        :ripple="false"
                                        :id="serviceButtonId(service.id)"
                                        v-on="on"
                                    >
                                        <span
                                            class="subheading text-none font-weight-bold"
                                            style="text-decoration: underline;"
                                            v-text="service['name' + upperLocale]"
                                        />
                                    </v-btn>
                                </template>
                                <span tabindex="-1" v-text="$t('appt.tooltips.clickToSee')" />
                            </v-tooltip>
                        </v-flex>

                        <v-spacer />

                        <!-- Service pdf icon if md+ -->
                        <service-pdf-link-icon
                            v-if="$vuetify.breakpoint.mdAndUp"
                            :service="service"
                        />

                        <!-- Selection button if md+ -->
                        <v-flex
                            v-if="$vuetify.breakpoint.mdAndUp"
                            class="text-right"
                        >
                            <previous-button
                                v-if="stepNumber > 1"
                                @previousStep="() => { $emit('previousStep') }"
                            />
                            <next-button
                                :aria-label="ariaLabelServiceSelected(service)"
                                :loading="isLoading"
                                :disabled="isLoading"
                                @nextStep="selectService(service.id)"
                            />
                        </v-flex>
                    </v-layout>
                </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
                <v-card
                    class="grey lighten-3"
                >
                    <!-- Service description -->
                    <v-card-text class="service-description pa-0">
                        <v-layout class="row">
                            <v-flex xs12>
                                <v-layout row wrap class="pt-3 px-3">
                                    <v-flex v-if="service && service.activateDurationInPublicPage" class="xs12 px-2 pb-0 pt-2">
                                        <span class="body-2">
                                            {{ $t('appt.duration') }}
                                        </span>
                                        <span>{{ service.length }} </span>
                                        <span>{{ $t('appt.minutes') }}</span>
                                    </v-flex>

                                    <v-flex v-if="service && service['enablePersonalDescription']" class="xs12 px-2 pt-0 pb-2">
                                        <br v-if="service && (service.activateDurationInPublicPage)" />
                                        <span class="body-2 d-sr-only">
                                            {{ $t('appt.description') }}
                                        </span>
                                        <span v-html="service['description' + upperLocale]" />
                                    </v-flex>

                                    <Tarification :service="service" class="tarification-component" />
                                </v-layout>
                            </v-flex>

                            <service-pdf-link-icon large-icon :service="service" />
                        </v-layout>
                    </v-card-text>

                    <!-- Selection button when less than medium format -->
                    <v-card-actions>
                        <v-layout class="row">
                            <v-flex xs12 class="text-center">
                                <previous-button
                                    v-if="stepNumber > 1"
                                    :allow-small="true"
                                    @previousStep="() => { $emit('previousStep') }"
                                />
                                <next-button
                                    :allow-small="true"
                                    :aria-label="ariaLabelServiceSelected(service)"
                                    :loading="isLoading"
                                    :disabled="isLoading"
                                    @nextStep="selectService(service.id)"
                                />
                            </v-flex>
                        </v-layout>
                    </v-card-actions>
                </v-card>
            </v-expansion-panel-content>
        </v-expansion-panel>
    </v-expansion-panels>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex';
import { filter, find, get, includes, isNil } from 'lodash';
import ServicePdfLinkIcon from '@/components/documents/ServicePdfLinkIcon';
import Tarification from '@/components/steppers/Tarification/Tarification';
import { analyticsEventHandler } from '@/mixins/ga-event-handler';
import { EstablishmentSetting } from '@/enums';
import PreviousButton from '@/components/buttons/PreviousButton.vue';
import NextButton from '@/components/buttons/NextButton.vue';

export default {
    name: 'step-service',
    components: {
        ServicePdfLinkIcon,
        Tarification,
        PreviousButton,
        NextButton
    },
    props: {
        loading: { type: Boolean, required: true },
        stepNumber: { type: Number, required: true }
    },
    mixins: [analyticsEventHandler],
    data() {
        return {
            currentPanel: 0,
            localLoading: false,
        };
    },
    computed: {
        ...mapGetters({
            upperLocale: 'upperLocale',
            getServices: 'services/getActivatedServices',
            getServiceHasServiceTemplateFilterIds: 'services/getServiceHasServiceTemplateFilterIds',
            getServicesFilteredByServiceTemplateIds: 'services/getServicesFilteredByServiceTemplateIds',
            getAssociatedEst: 'services/getAssociatedEst',
            isFirstServiceLoad: 'services/getFirstServiceLoad',
            getEstablishmentSettingById: 'establishment/getEstablishmentSettingById',
        }),
        selectedService: {
            get: function() {
                return this.$store.getters['services/getSelectedService'];
            },
            set: function(value) {
                this.$store.commit('services/SET_SELECTED_SERVICE', value);
            },
        },
        isLoading() {
            return this.loading || this.localLoading;
        },
        filteredServices() {
            const associatedEst = this.getAssociatedEst;
            let services = [];
            if (this.getServiceHasServiceTemplateFilterIds) {
                services = this.getServicesFilteredByServiceTemplateIds;
            }
            else {
                services = associatedEst.length === 0
                    ? this.getServices
                    : filter(this.getServices, (service) => {
                        return includes(associatedEst, service.id);
                    });
            }

            if (services.length === 1 && this.isFirstServiceLoad) {
                this.$store.commit('services/SET_FIRST_SERVICE_LOAD', false);
                this.selectService(get(services[0], 'id'));
                return;
            }

            return services;
        },
    },
    watch: {
        currentPanel(val) {
            this.setService(val);
        },
        selectedService(service) {
            if (isNil(service)) {
                return;
            }
            this.currentPanel = get(service, 'id');
        },
    },
    methods: {
        ...mapActions({
            getEstMetaData: 'services/getEstMetaData',
            getEstSettings: 'services/getEstSettings',
            removeInstantiateServiceWithServiceTemplateIds: 'services/removeInstantiateServiceWithServiceTemplateIds',
        }),
        ...mapMutations({
            setServiceHasDataCollectionFormId: 'services/SET_SERVICE_HAS_DATA_COLLECTION_FORM_ID',
        }),

        ariaLabelServiceSelected(service) {
            return this.$t('appt.accessibility.selectService', { serviceName: service['name' + this.upperLocale], serviceDescription: service['description' + this.upperLocale] ?? this.$t('appt.accessibility.unknowServiceDescription') });
        },

        /**
         * Selects the service and moves to the next step
         */
        async selectService(serviceId) {
            this.setService(serviceId);
            // GTM event
            await this.publicSelectServiceAnalytics();
            await this.nextStep();
        },

        /**
         * Set the current service object
         * @param serviceId
         */
        setService(serviceId) {
            this.selectedService = !isNil(serviceId) && serviceId >= 0 ? find(this.getServices, ['id', serviceId]) : null;
        },

        /**
         * nextStep implementation for service step
         * @returns {Promise<void>}
         */
        async nextStep() {
            try {
                this.localLoading = true;
                await Promise.all([
                    this.getEstMetaData([this.selectedService.id]),
                    this.getEstSettings([this.selectedService.id]),
                ]);

                // validate if service has data collection form id
                if (this.getEstablishmentSettingById(EstablishmentSetting.ENABLE_DATA_COLLECTION_FORMS)) {
                    await this.setServiceHasDataCollectionFormId();
                }

                this.$emit('nextStep');
            }
            finally {
                this.localLoading = false;
            }
        },

        serviceButtonId(serviceId) {
            return `service-name-button-${serviceId}`;
        },
    },
};
</script>

<style lang="scss" scoped>
    @import './src/assets/scss/variables';

    .v-expansion-panel-odd-background {
        &.active-header-background {
            background-color: var(--v-primary-base) !important;
        }

        &:nth-child(even) {
            background-color: #ddd;
        }
    }
    .tarification-component {
        margin: auto;
    }
    .v-expansion-panel-odd-background {
        &.active-header-background {
            background-color: $primary-base !important;
        }
    }
    .v-expansion-panel-header--active .button-continue-title{
        display: none;
    }

</style>
